import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {Link, useLocation, useNavigate} from 'react-router-dom';

import {
    Box,
    Collapse,
    Divider,
    Drawer,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    TextField,
    Typography,
    styled,
} from '@mui/material';

import {NavbarConfig} from './NavbarConfig';
import {usePermissionLookup} from '../../permission/PermissionUtil';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';

const DrawerHeader = styled(Box)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
}));

const byLabel = (a, b) => a.label.localeCompare(b.label);

const filterAndSortNavbar = (config, permissionCheck) => {
    const filtered = [];

    for (const item of config) {
        if (item.permission && !permissionCheck(item.permission)) {
            continue;
        }

        if (item.items) {
            const childItems = item.items
                .filter(child => !child.permission || permissionCheck(child.permission))
                .sort(byLabel);
            if (childItems.length) {
                filtered.push({...item, items: childItems});
            }
        } else {
            filtered.push(item);
        }
    }

    return filtered.sort(byLabel);
};

const matches = (label, term) => label.toLowerCase().includes(term);

// Filters the (already permission-filtered) navbar by a search term. A group is
// kept either when its own label matches (keeping all children) or when some of
// its children match (keeping only those).
const filterBySearch = (config, rawTerm) => {
    const term = rawTerm.trim().toLowerCase();
    if (!term) {
        return config;
    }

    const result = [];
    for (const item of config) {
        const selfMatch = matches(item.label, term);
        if (item.items) {
            const children = selfMatch
                ? item.items
                : item.items.filter(child => matches(child.label, term));
            if (children.length) {
                result.push({...item, items: children});
            }
        } else if (selfMatch) {
            result.push(item);
        }
    }
    return result;
};

const Navbar = ({isVisible, onHide}) => {
    const [expandedLabel, setExpandedLabel] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [externalLinks, setExternalLinks] = useState([]);

    const searchInputRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();
    const permissionCheck = usePermissionLookup();

    const permittedNavbar = useMemo(
        () => filterAndSortNavbar(NavbarConfig, permissionCheck),
        [permissionCheck]
    );

    const visibleNavbar = useMemo(
        () => filterBySearch(permittedNavbar, searchTerm),
        [permittedNavbar, searchTerm]
    );

    const visibleExternalLinks = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return externalLinks;
        }
        return externalLinks.filter(link => matches(link.label, term));
    }, [externalLinks, searchTerm]);

    useEffect(() => {
        const fetchExternalLinks = async () => {
            const response = await axios.get('/externallink');
            setExternalLinks(response.data);
        };

        fetchExternalLinks();
    }, []);

    // Focus the search box each time the drawer opens (the persistent drawer
    // stays mounted, so autoFocus alone would only fire once).
    useEffect(() => {
        if (isVisible) {
            searchInputRef.current?.focus();
        }
    }, [isVisible]);

    // Auto-expand the group containing the active route so the current page is
    // always visible when the drawer opens. We search the stable NavbarConfig
    // constant (not the memoized permittedNavbar) and depend only on the path:
    // permittedNavbar changes identity on every render because usePermissionLookup
    // returns a fresh function each time, so depending on it here would re-run this
    // effect every render and continually clamp expandedLabel back to the active
    // group — freezing expand/collapse for any page that lives inside a group.
    useEffect(() => {
        const activeGroup = NavbarConfig.find(
            item => item.items?.some(child => child.path === location.pathname)
        );
        if (activeGroup) {
            setExpandedLabel(activeGroup.label);
        }
    }, [location.pathname]);

    const searching = searchTerm.trim().length > 0;
    const isGroupOpen = (label) => searching || expandedLabel === label;

    const handleClose = () => {
        onHide();
    };

    const handleNavigate = (path) => {
        handleClose();
        navigate(path);
    };

    const handleParentItemClick = (item) => {
        if (item.path) {
            handleNavigate(item.path);
        } else {
            setExpandedLabel(prev => (prev === item.label ? null : item.label));
        }
    };

    const hasResults = visibleNavbar.length > 0 || visibleExternalLinks.length > 0;

    return (
        <Drawer
            variant="persistent"
            open={isVisible}
            anchor="left"
            onClose={handleClose}
            sx={{
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: 250,
                },
            }}
        >
            <DrawerHeader>
                <IconButton onClick={handleClose} aria-label="Close menu">
                    <ChevronLeftIcon/>
                </IconButton>
            </DrawerHeader>
            <Box sx={{px: 2, pb: 1}}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search menu…"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    inputRef={searchInputRef}
                    inputProps={{'aria-label': 'Search menu'}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small"/>
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    aria-label="Clear search"
                                    onClick={() => setSearchTerm('')}
                                >
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Divider/>
            <List sx={{overflowY: 'auto', py: 0}}>
                {visibleNavbar.map((item) => (
                    <React.Fragment key={item.label}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => handleParentItemClick(item)}
                                selected={location.pathname === item.path}
                                aria-expanded={item.items ? isGroupOpen(item.label) : undefined}
                                aria-controls={item.items ? `${item.label}-sublist` : undefined}
                            >
                                <ListItemText primary={item.label} sx={{m: 0}}/>
                                {item.items &&
                                    (isGroupOpen(item.label) ? <ExpandLess/> : <ExpandMore/>)}
                            </ListItemButton>
                        </ListItem>
                        {item.items && (
                            <Collapse
                                in={isGroupOpen(item.label)}
                                timeout="auto"
                                unmountOnExit
                                id={`${item.label}-sublist`}
                            >
                                <List component="div" disablePadding>
                                    {item.items.map((subItem) => (
                                        <ListItem key={subItem.label} disablePadding sx={{pl: 4}}>
                                            <ListItemButton
                                                onClick={handleClose}
                                                component={Link}
                                                to={subItem.path}
                                                selected={location.pathname === subItem.path}
                                            >
                                                <ListItemText primary={subItem.label} sx={{m: 0}}/>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        )}
                    </React.Fragment>
                ))}

                {visibleExternalLinks.length > 0 && (
                    <>
                        <Divider sx={{my: 1}}/>
                        <ListSubheader disableSticky sx={{lineHeight: '2rem'}}>
                            Links
                        </ListSubheader>
                        {visibleExternalLinks.map((link) => (
                            <ListItem key={link.identifier} disablePadding>
                                <ListItemButton
                                    onClick={handleClose}
                                    component="a"
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <ListItemText primary={link.label} sx={{m: 0}}/>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </>
                )}

                {searching && !hasResults && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{px: 2, py: 2, textAlign: 'center'}}
                    >
                        No matches for “{searchTerm.trim()}”
                    </Typography>
                )}
            </List>
        </Drawer>
    );
};

Navbar.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
};

export default Navbar;
